import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  IconButton,
  useToast,
  Badge,
  Flex,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import {
  postDataSuperAdmin,
  updateDataSuperAdmin,
} from "../../redux/actions/common.action";

const SECTION_OPTIONS = [
  { value: "terms", label: "Terms of Service" },
  { value: "privacy", label: "Privacy Policy" },
  { value: "security", label: "Security Policy" },
  { value: "refund", label: "Refund Policy" },
];

const AddEditLegalContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: any = useDispatch();
  const toast = useToast();

  // Modal controls
  const {
    isOpen: isTopicDeleteOpen,
    onOpen: onTopicDeleteOpen,
    onClose: onTopicDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isSubTopicDeleteOpen,
    onOpen: onSubTopicDeleteOpen,
    onClose: onSubTopicDeleteClose,
  } = useDisclosure();

  const { loading } = useSelector((state: any) => state.common);

  const locationData = location.state?.contentData;

  const [section, setSection] = useState("");
  const [topics, setTopics]: any = useState([
    {
      topicTitle: "",
      topicId: "",
      content: "",
      subTopics: [],
    },
  ]);
  const [editorKey, setEditorKey] = useState(0);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Delete state
  const [deleteTopicIndex, setDeleteTopicIndex] = useState<number | null>(null);
  const [deleteSubTopicIndices, setDeleteSubTopicIndices] = useState<{
    topicIndex: number;
    subIndex: number;
  } | null>(null);

  useEffect(() => {
    if (id && locationData) {
      setSection(locationData.section || "");
      setTopics(
        locationData.topics?.length > 0
          ? locationData.topics.map((topic: any) => ({
              topicTitle: topic.topicTitle || "",
              topicId: topic.topicId || "",
              content: topic.content || "",
              subTopics: topic.subTopics || [],
            }))
          : [{ topicTitle: "", topicId: "", content: "", subTopics: [] }]
      );
      setTimeout(() => {
        setEditorKey((prev) => prev + 1);
        setIsDataLoaded(true);
      }, 100);
    } else if (!id) {
      setIsDataLoaded(true);
    }
  }, [id, locationData]);

  const generateTopicId = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleAddTopic = () => {
    setTopics([
      ...topics,
      {
        topicTitle: "",
        topicId: "",
        content: "",
        subTopics: [],
      },
    ]);
    setEditorKey((prev) => prev + 1);
  };

  const handleRemoveTopicClick = (index: number) => {
    setDeleteTopicIndex(index);
    onTopicDeleteOpen();
  };

  const confirmRemoveTopic = () => {
    if (deleteTopicIndex !== null && topics.length > 1) {
      setTopics(topics.filter((_:any, i:any) => i !== deleteTopicIndex));
      setEditorKey((prev) => prev + 1);
    }
    onTopicDeleteClose();
    setDeleteTopicIndex(null);
  };

  const handleTopicChange = (index: number, field: string, value: string) => {
    const updatedTopics = [...topics];
    updatedTopics[index] = {
      ...updatedTopics[index],
      [field]: value,
    };

    if (field === "topicTitle") {
      updatedTopics[index].topicId = generateTopicId(value);
    }

    setTopics(updatedTopics);
  };

  const handleAddSubTopic = (topicIndex: number) => {
    const updatedTopics: any = [...topics];
    if (!updatedTopics[topicIndex].subTopics) {
      updatedTopics[topicIndex].subTopics = [];
    }
    updatedTopics[topicIndex].subTopics.push({
      title: "",
      content: "",
    });
    setTopics(updatedTopics);
    setEditorKey((prev) => prev + 1);
  };

  const handleRemoveSubTopicClick = (topicIndex: number, subIndex: number) => {
    setDeleteSubTopicIndices({ topicIndex, subIndex });
    onSubTopicDeleteOpen();
  };

  const confirmRemoveSubTopic = () => {
    if (deleteSubTopicIndices) {
      const { topicIndex, subIndex } = deleteSubTopicIndices;
      const updatedTopics = [...topics];
      updatedTopics[topicIndex].subTopics = updatedTopics[
        topicIndex
      ].subTopics.filter((_: any, i: number) => i !== subIndex);
      setTopics(updatedTopics);
      setEditorKey((prev) => prev + 1);
    }
    onSubTopicDeleteClose();
    setDeleteSubTopicIndices(null);
  };

  const handleSubTopicChange = (
    topicIndex: number,
    subIndex: number,
    field: string,
    value: string
  ) => {
    const updatedTopics: any = [...topics];
    updatedTopics[topicIndex].subTopics[subIndex] = {
      ...updatedTopics[topicIndex].subTopics[subIndex],
      [field]: value,
    };
    setTopics(updatedTopics);
  };

  const validateForm = () => {
    if (!section) {
      toast({
        title: "Section is required",
        status: "error",
        position: "top-right",
        isClosable: true,
      });
      return false;
    }

    for (let i = 0; i < topics.length; i++) {
      if (!topics[i].topicTitle) {
        toast({
          title: `Topic ${i + 1} title is required`,
          status: "error",
          position: "top-right",
          isClosable: true,
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload = {
      section,
      topics: topics.map((topic: any, index: any) => ({
        ...topic,
        order: index,
        subTopics:
          topic.subTopics?.map((sub: any, subIndex: number) => ({
            ...sub,
            order: subIndex,
          })) || [],
      })),
    };

    try {
      if (id) {
        await dispatch(
          updateDataSuperAdmin(
            id,
            "legal-content",
            payload,
            navigate,
            toast
          ) as any
        );
      } else {
        await dispatch(
          postDataSuperAdmin(payload, "legal-content", navigate, toast) as any
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <Box p={{ base: "4px", md: "8px" }} bg="gray.50" minH="100vh">
      <style>
        {`
          .ck-editor__editable {
            min-height: 200px !important;
          }
          
          .ck-editor__editable_inline {
            min-height: 200px !important;
            max-height: 400px;
            overflow-y: auto;
          }

          .subtopic-editor .ck-editor__editable {
            min-height: 180px !important;
          }

          .subtopic-editor .ck-editor__editable_inline {
            min-height: 180px !important;
            max-height: 350px;
          }
        `}
      </style>

      {/* Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        p={{ base: "8px", md: "12px" }}
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <IconButton
              aria-label="Go back"
              icon={<ArrowBackIcon />}
              onClick={() => navigate("/legal-content-list")}
              variant="ghost"
              size="sm"
              _hover={{ bg: "gray.100" }}
            />
          </HStack>
          <Badge
            colorScheme={id ? "blue" : "green"}
            fontSize="xs"
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="medium"
          >
            {id ? "Edit Mode" : "Create Mode"}
          </Badge>
        </Flex>
      </Box>

      <form onSubmit={handleSubmit}>
        {/* Section Selection */}
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
          mb="8px"
          p={{ base: 3, md: 4 }}
        >
          <FormControl isRequired>
            <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
              Select Section
            </FormLabel>
            <Select
              value={section}
              onChange={(e) => setSection(e.target.value)}
              placeholder="Choose a section"
              size="sm"
              fontSize="xs"
              isDisabled={!!id}
            >
              {SECTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Topics */}
        {isDataLoaded && (
          <Accordion allowMultiple defaultIndex={[0]}>
            {topics.map((topic: any, topicIndex: any) => (
              <Box
                key={`topic-${topicIndex}-${editorKey}`}
                bg="white"
                borderRadius="xl"
                boxShadow="sm"
                border="1px"
                borderColor="gray.200"
                mb="8px"
                overflow="hidden"
              >
                <AccordionItem border="none">
                  <AccordionButton
                    p={{ base: 3, md: 4 }}
                    _hover={{ bg: "gray.50" }}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <HStack flex="1" spacing={3}>
                      <Badge
                        colorScheme="purple"
                        fontSize="xs"
                        px={2}
                        py={1}
                        borderRadius="full"
                      >
                        Topic {topicIndex + 1}
                      </Badge>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color="gray.700"
                        noOfLines={1}
                      >
                        {topic.topicTitle || "Untitled Topic"}
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      {topics.length > 1 && (
                        <IconButton
                          aria-label="Remove topic"
                          icon={<DeleteIcon />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveTopicClick(topicIndex);
                          }}
                        />
                      )}
                      <AccordionIcon />
                    </HStack>
                  </AccordionButton>

                  <AccordionPanel p={{ base: 3, md: 4 }} pt={0}>
                    <VStack spacing={4} align="stretch">
                      {/* Topic Title and ID in same row for lg+ */}
                      <Flex direction={{ base: "column", lg: "row" }} gap={4}>
                        <FormControl isRequired flex="1">
                          <FormLabel fontSize="xs" fontWeight="medium">
                            Topic Title*
                          </FormLabel>
                          <Input
                            value={topic.topicTitle}
                            onChange={(e) =>
                              handleTopicChange(
                                topicIndex,
                                "topicTitle",
                                e.target.value
                              )
                            }
                            placeholder="Enter topic title"
                            size="sm"
                            fontSize="xs"
                          />
                        </FormControl>

                        <FormControl flex="1">
                          <FormLabel fontSize="xs" fontWeight="medium">
                            Topic ID (Auto-generated)
                          </FormLabel>
                          <Input
                            value={topic.topicId}
                            isReadOnly
                            size="sm"
                            fontSize="xs"
                            bg="gray.50"
                          />
                        </FormControl>
                      </Flex>

                      {/* Topic Content */}
                      <FormControl>
                        <FormLabel fontSize="xs" fontWeight="medium">
                          Topic Content (Optional)
                        </FormLabel>
                        <Box
                          border="1px"
                          borderColor="gray.300"
                          borderRadius="md"
                          overflow="hidden"
                        >
                          <CKEditor
                            key={`topic-editor-${topicIndex}-${editorKey}`}
                            editor={ClassicEditor}
                            data={topic.content || ""}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              handleTopicChange(topicIndex, "content", data);
                            }}
                            config={{
                              toolbar: [
                                "heading",
                                "|",
                                "bold",
                                "italic",
                                "link",
                                "bulletedList",
                                "numberedList",
                                "|",
                                "blockQuote",
                                "insertTable",
                                "|",
                                "undo",
                                "redo",
                              ],
                            }}
                            onReady={(editor) => {
                              console.log("Topic editor ready", topicIndex);
                            }}
                          />
                        </Box>
                      </FormControl>

                      {/* Sub Topics Section */}
                      <Box>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          mb={3}
                          color="gray.700"
                        >
                          Sub Topics
                        </Text>

                        {topic.subTopics?.map(
                          (subTopic: any, subIndex: number) => (
                            <Box
                              key={`subtopic-${topicIndex}-${subIndex}-${editorKey}`}
                              border="1px"
                              borderColor="gray.200"
                              borderRadius="md"
                              p={{ base: 2, md: 3 }}
                              mb={3}
                              bg="gray.50"
                            >
                              <HStack justify="space-between" mb={3}>
                                <Badge colorScheme="green" fontSize="xs">
                                  Sub Topic {subIndex + 1}
                                </Badge>
                                <IconButton
                                  aria-label="Remove sub topic"
                                  icon={<DeleteIcon />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() =>
                                    handleRemoveSubTopicClick(
                                      topicIndex,
                                      subIndex
                                    )
                                  }
                                />
                              </HStack>

                              <VStack spacing={3} align="stretch">
                                <FormControl isRequired>
                                  <FormLabel fontSize="xs" fontWeight="medium">
                                    Sub Topic Title*
                                  </FormLabel>
                                  <Input
                                    value={subTopic.title}
                                    onChange={(e) =>
                                      handleSubTopicChange(
                                        topicIndex,
                                        subIndex,
                                        "title",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Enter sub topic title"
                                    size="sm"
                                    fontSize="xs"
                                    bg="white"
                                  />
                                </FormControl>

                                <FormControl isRequired>
                                  <FormLabel fontSize="xs" fontWeight="medium">
                                    Sub Topic Content*
                                  </FormLabel>
                                  <Box
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    overflow="hidden"
                                    bg="white"
                                    className="subtopic-editor"
                                  >
                                    <CKEditor
                                      key={`subtopic-editor-${topicIndex}-${subIndex}-${editorKey}`}
                                      editor={ClassicEditor}
                                      data={subTopic.content || ""}
                                      onChange={(event, editor) => {
                                        const data = editor.getData();
                                        handleSubTopicChange(
                                          topicIndex,
                                          subIndex,
                                          "content",
                                          data
                                        );
                                      }}
                                      config={{
                                        toolbar: [
                                          "heading",
                                          "|",
                                          "bold",
                                          "italic",
                                          "link",
                                          "bulletedList",
                                          "numberedList",
                                          "|",
                                          "blockQuote",
                                          "insertTable",
                                          "|",
                                          "undo",
                                          "redo",
                                        ],
                                      }}
                                      onReady={(editor) => {
                                        console.log(
                                          "Subtopic editor ready",
                                          topicIndex,
                                          subIndex
                                        );
                                      }}
                                    />
                                  </Box>
                                </FormControl>
                              </VStack>
                            </Box>
                          )
                        )}

                        {/* Add Sub Topic Button - Below subtopics */}
                        <Button
                          leftIcon={<AddIcon />}
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => handleAddSubTopic(topicIndex)}
                          mt={topic.subTopics?.length > 0 ? 0 : 0}
                        >
                          Add Sub Topic
                        </Button>
                      </Box>
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              </Box>
            ))}
          </Accordion>
        )}

        {/* Add Topic Button - Smaller width */}
        <Flex justify={{ base: "stretch", md: "flex-start" }} mb="8px">
          <Button
            leftIcon={<AddIcon />}
            onClick={handleAddTopic}
            size="sm"
            fontSize="xs"
            variant="outline"
            borderColor="blue.500"
            color="blue.500"
            _hover={{ bg: "blue.50" }}
            w={{ base: "full", md: "200px" }}
          >
            Add Topic
          </Button>
        </Flex>

        {/* Action Buttons */}
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
          p={{ base: 2, md: 3 }}
        >
          <HStack spacing={2} justify="flex-end" flexWrap="wrap">
            <Button
              variant="outline"
              onClick={() => navigate("/legal-content-list")}
              size="sm"
              fontSize="xs"
              px={6}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              loadingText={id ? "Updating..." : "Creating..."}
              size="sm"
              fontSize="xs"
              px={6}
            >
              {id ? "Update" : "Create"}
            </Button>
          </HStack>
        </Box>
      </form>

      {/* Delete Topic Confirmation Modal */}
      <Modal
        isOpen={isTopicDeleteOpen}
        onClose={onTopicDeleteClose}
        isCentered
        size="md"
      >
        <ModalOverlay />
        <ModalContent mx={{ base: 4, md: 0 }}>
          <ModalHeader fontSize="md" fontWeight="semibold">
            Delete Topic
          </ModalHeader>
          <ModalBody>
            <VStack align="start" spacing={3}>
              <Text fontSize="sm" color="gray.700">
                Are you sure you want to delete this topic?
              </Text>
              {deleteTopicIndex !== null && (
                <Box
                  bg="red.50"
                  p={3}
                  borderRadius="md"
                  border="1px"
                  borderColor="red.200"
                  w="full"
                >
                  <Text fontSize="sm" fontWeight="semibold" color="red.800">
                    Topic {deleteTopicIndex + 1}:{" "}
                    {topics[deleteTopicIndex]?.topicTitle || "Untitled Topic"}
                  </Text>
                </Box>
              )}
              <Text fontSize="xs" color="gray.500">
                This will permanently remove the topic and all its subtopics.
                This action cannot be undone.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button
                variant="outline"
                size="sm"
                onClick={onTopicDeleteClose}
                fontSize="xs"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                size="sm"
                onClick={confirmRemoveTopic}
                fontSize="xs"
              >
                Delete
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Sub Topic Confirmation Modal */}
      <Modal
        isOpen={isSubTopicDeleteOpen}
        onClose={onSubTopicDeleteClose}
        isCentered
        size="md"
      >
        <ModalOverlay />
        <ModalContent mx={{ base: 4, md: 0 }}>
          <ModalHeader fontSize="md" fontWeight="semibold">
            Delete Sub Topic
          </ModalHeader>
          <ModalBody>
            <VStack align="start" spacing={3}>
              <Text fontSize="sm" color="gray.700">
                Are you sure you want to delete this sub topic?
              </Text>
              {deleteSubTopicIndices && (
                <Box
                  bg="red.50"
                  p={3}
                  borderRadius="md"
                  border="1px"
                  borderColor="red.200"
                  w="full"
                >
                  <Text fontSize="sm" fontWeight="semibold" color="red.800">
                    Sub Topic {deleteSubTopicIndices.subIndex + 1}:{" "}
                    {topics[deleteSubTopicIndices.topicIndex]?.subTopics[
                      deleteSubTopicIndices.subIndex
                    ]?.title || "Untitled Sub Topic"}
                  </Text>
                </Box>
              )}
              <Text fontSize="xs" color="gray.500">
                This will permanently remove the sub topic. This action cannot
                be undone.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button
                variant="outline"
                size="sm"
                onClick={onSubTopicDeleteClose}
                fontSize="xs"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                size="sm"
                onClick={confirmRemoveSubTopic}
                fontSize="xs"
              >
                Delete
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AddEditLegalContent;
